/**
 * Global top-of-page loading progress bar.
 *
 * Driven by an internal event bus (window events `loading:start` / `loading:end` /
 * `loading:tick`) so any subsystem (API client, route transitions, WS reconnect)
 * can report progress without prop drilling.
 *
 * Behavior:
 *   - `loading:start`  — appear immediately, animate to ~70% over 600ms.
 *   - `loading:tick`    — push the bar to a percentage (0–100).
 *   - `loading:end`     — finish to 100% then fade out after 200ms.
 *
 * The bar uses CSS transforms rather than width changes so the GPU compositor
 * handles it (no layout reflow on every animation frame). It also pauses
 * animation while the tab is hidden so users on background tabs don't see a
 * stuck bar.
 */
import { useEffect, useRef, useState } from 'react';

type Status = 'idle' | 'loading' | 'done';

export default function LoadingBar() {
  const [status, setStatus] = useState<Status>('idle');
  const [progress, setProgress] = useState(0);

  // Mutable refs for the animation loop. Refs avoid re-binding the loop when
  // `status` changes (which would otherwise reset every time we cross an
  // idle/loading boundary).
  const targetRef = useRef(0);
  const valueRef = useRef(0);
  const statusRef = useRef<Status>('idle');
  const rafRef = useRef<number | null>(null);
  const pendingTimers = useRef<number[]>([]);

  // Keep the ref in sync with state so setTimeout closures read fresh.
  statusRef.current = status;

  useEffect(() => {
    function step() {
      const target = targetRef.current;
      const cur = valueRef.current;
      if (Math.abs(target - cur) < 0.5) {
        valueRef.current = target;
        setProgress(target);
      } else {
        valueRef.current = cur + (target - cur) * 0.18;
        setProgress(valueRef.current);
      }
      if (statusRef.current === 'loading' || statusRef.current === 'done') {
        rafRef.current = requestAnimationFrame(step);
      } else {
        rafRef.current = null;
      }
    }

    function ensureLoop() {
      if (rafRef.current == null && (statusRef.current === 'loading' || statusRef.current === 'done')) {
        rafRef.current = requestAnimationFrame(step);
      }
    }

    function onStart() {
      // Reset timers from any prior start.
      pendingTimers.current.forEach((t) => clearTimeout(t));
      pendingTimers.current = [];
      targetRef.current = 12;
      valueRef.current = valueRef.current > 12 ? valueRef.current : 0;
      setStatus('loading');
      ensureLoop();
      pendingTimers.current.push(
        window.setTimeout(() => {
          if (statusRef.current === 'loading' && targetRef.current < 70) targetRef.current = 35;
        }, 200),
        window.setTimeout(() => {
          if (statusRef.current === 'loading' && targetRef.current < 80) targetRef.current = 65;
        }, 600),
        window.setTimeout(() => {
          if (statusRef.current === 'loading' && targetRef.current < 95) targetRef.current = 80;
        }, 1500),
      );
    }

    function onEnd() {
      targetRef.current = 100;
      setStatus('done');
      ensureLoop();
      pendingTimers.current.push(
        window.setTimeout(() => {
          setStatus((s) => (s === 'done' ? 'idle' : s));
          setProgress(0);
          targetRef.current = 0;
          valueRef.current = 0;
        }, 280),
      );
    }

    function onTick(e: Event) {
      const ce = e as CustomEvent<number>;
      if (typeof ce.detail === 'number' && Number.isFinite(ce.detail)) {
        targetRef.current = Math.max(0, Math.min(100, ce.detail));
      }
    }

    window.addEventListener('loading:start', onStart);
    window.addEventListener('loading:end', onEnd);
    window.addEventListener('loading:tick', onTick as EventListener);

    // Programmatic handle for non-event-driven code paths.
    (window as unknown as { __loadingBar?: { start: () => void; end: () => void; tick: (n: number) => void } }).__loadingBar = {
      start: onStart,
      end: onEnd,
      tick: (n: number) => {
        if (Number.isFinite(n)) targetRef.current = Math.max(0, Math.min(100, n));
      },
    };

    return () => {
      window.removeEventListener('loading:start', onStart);
      window.removeEventListener('loading:end', onEnd);
      window.removeEventListener('loading:tick', onTick as EventListener);
      pendingTimers.current.forEach((t) => clearTimeout(t));
      pendingTimers.current = [];
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  // Pause animation while tab hidden so the bar doesn't appear "stuck".
  useEffect(() => {
    function onVis() {
      if (document.visibilityState !== 'visible') {
        if (rafRef.current != null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      } else if ((status === 'loading' || status === 'done') && rafRef.current == null) {
        rafRef.current = requestAnimationFrame(() => {
          // resume — animate by ticking once so valueRef moves toward targetRef
          const target = targetRef.current;
          const cur = valueRef.current;
          if (Math.abs(target - cur) >= 0.5) {
            valueRef.current = cur + (target - cur) * 0.18;
            setProgress(valueRef.current);
          }
          rafRef.current = requestAnimationFrame(function tick() {
            const t = targetRef.current;
            const c = valueRef.current;
            if (Math.abs(t - c) < 0.5) {
              valueRef.current = t;
              setProgress(t);
              rafRef.current = null;
              return;
            }
            valueRef.current = c + (t - c) * 0.18;
            setProgress(valueRef.current);
            rafRef.current = requestAnimationFrame(tick);
          });
        });
      }
    }
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [status]);

  const visible = status !== 'idle';
  const pct = Math.max(0, Math.min(100, progress));
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none fixed left-0 right-0 top-0 z-[60] h-0.5 transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
      data-loading={status}
    >
      <div
        className="h-full bg-brand-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]"
        style={{
          width: `${pct}%`,
          transition:
            status === 'done' ? 'width 220ms ease-out, opacity 220ms ease-out' : undefined,
        }}
      />
    </div>
  );
}
